import React, {useState, useRef, useEffect, useContext} from 'react';
import './SelectInput.scss';
import {I18nContext} from '../../contexts/i18nContext';
import {constructClassNames} from '../../helpers/helpers';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CancelIcon from '@mui/icons-material/Cancel';

const SelectInput = (
  {
    name,
    options=[],
    value,
    onSelect,
    label,
    placeholder,
    required,
    multiple,
    small,
    noMargin,
    disabled,
    filter,
    inline,
    dropdownOnTop,
    mini
  }
) => {
  const { t } = useContext(I18nContext);
  const useOutsideAlerter = ref => {
    useEffect(() => {
      const handleClickOutside = event => {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowOptions(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const selectRef = useRef(null);
  useOutsideAlerter(selectRef);

  const [showOptions, setShowOptions] = useState(false);
  const [showSelectedTooltip, setShowSelectedTooltip] = useState(false);

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const openSelectedTooltip = () => {
    setShowSelectedTooltip(true);
  };

  const closeSelectedTooltip = () => {
    setShowSelectedTooltip(false);
  };

  const handleSelect = option => {
    onSelect(name, option, multiple, true, 'select');
    if (!multiple) {
      toggleOptions();
    }
  };

  const handleClear = () => {
    onSelect(name, "", multiple, true, 'select');
  };

  const classes = [
    {name: 'noMargin', condition: !!noMargin},
    {name: 'inline', condition: !!inline},
    {name: 'mini', condition: !!mini}
  ];

  const labelClasses = [
    {name: 'full', condition: !!inline}
  ];

  const valueFieldClasses = [
    {name: 'placeholder', condition: !value || !value?.length},
    {name: 'small', condition: !!small},
    {name: 'disabled', condition: !!disabled},
    {name: 'emptyRequired', condition: (!value || !value?.length) && required}
  ];

  const optionListClasses = [
    {name: 'noLabel', condition: !label || !!inline},
    {name: 'onTop', condition: !!dropdownOnTop}
  ];

  return (
    <div className={constructClassNames('SelectInput', classes)} ref={selectRef}>
      {!!label && (
        <label className={constructClassNames('inputLabel', labelClasses)}>
          {label}{required ? '*' : ''}
        </label>
      )}
      <div
        className={constructClassNames('valueField', valueFieldClasses)}
        onClick={!disabled ? toggleOptions : null}
        onMouseEnter={openSelectedTooltip}
        onMouseOver={openSelectedTooltip}
        onMouseOut={closeSelectedTooltip}
        onMouseLeave={closeSelectedTooltip}
      >
        {multiple
          ? (value.length ? value.map(val => options.find(x => x.id === val)?.label).join(', ') : placeholder)
          : options.find(x => x.id === value)?.label ?? placeholder
        }
        <div className='dropdownIcon'>
          {showOptions ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </div>
      </div>
      {filter && !!value && (
        <div className='clearIcon' onClick={handleClear}>
          <CancelIcon />
        </div>
      )}
      {showOptions && (
        <ul className={constructClassNames('optionsList', optionListClasses)}>
          {options.filter(x => !x.deleted).map((option, index) => (
            <li
              key={index}
              className={`
                selectOption 
                ${(multiple ? value.includes(option.id) : value === option.id) 
                  ? 'selectOption--selected' 
                  : ''
                }
              `}
              onClick={() => handleSelect(option.id)}
            >
              {option.label}
            </li>
          ))}
          {!options.length && (
            <li className='noOptionsPlaceholder'>{t('no options')}</li>
          )}
        </ul>
      )}
      {multiple && showSelectedTooltip && !showOptions && value.length > 1 && (
        <div className='selectedOptionsTooltip'>
          {value.map(val => <div className='tooltipItem' key={val}>{options.find(x => x.id === val).label}</div>)}
        </div>
      )}
    </div>
  );
}

export default SelectInput;
